import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mountAccountDialog,
  type AccountArtUrls,
  type AccountDialogHandle,
  type AccountPress,
  type AccountView,
  type FieldRect,
} from '@goodtime/engine';
import envelopeUrl from '../assets/account/envelope.png';
import tickOffUrl from '../assets/account/tick_box.png';
import tickOnUrl from '../assets/account/tick_box_on.png';
import floorUrl from '../assets/reel/forest_column_bg.png';
import {
  fetchAccountStatus,
  followSignInLink,
  requestSignInLink,
  setMarketingOptIn,
  type AccountStatus,
} from '../api/accounts';
import { ApiError } from '../api/client';
import { adoptPlayerId } from '../api/players';
import { noteAccountMoment, onAccountMoment, onOpenAccountBoard, publishAccountStatus } from '../lib/accountEvents';
import {
  offerAllowed,
  readOfferRecord,
  recordDismissed,
  recordShown,
  writeOfferRecord,
  type AccountMoment,
} from '../lib/accountOffers';
import { ADOPTED_KEY, readLaunchSignInToken, SIGNED_IN_KEY } from '../lib/signInLink';
import { BUTTON_PLATES } from '../themes/roadGames';

const ART: AccountArtUrls = { floor: floorUrl, envelope: envelopeUrl, tickOff: tickOffUrl, tickOn: tickOnUrl };
const EMAIL = /^[^@\s]{1,64}@[^@\s.]+(\.[^@\s.]+)+$/;
/** How long a sign-in link works: the server's app.accounts.link-ttl
 *  (24 hours since 20 October 2026). */
const LINK_MINUTES = 24 * 60;
/** How long after a moment the offer waits, so the moment itself (a win's
 *  count-up, a new world's reveal) plays out first. */
const SETTLE_MS: Record<AccountMoment, number> = {
  'big-win': 3500,
  'level-up': 2500,
  'new-world': 4800,
  // The restore moment's celebration runs a few seconds.
  chapter: 6000,
};
/** Give up on an offer if the game stays busy this long. */
const WAIT_LIMIT_MS = 30_000;

declare global {
  interface Window {
    /** The account layer, for browser tests: open it as Settings does, or
     *  report a good moment. */
    __accounts?: { open: () => void; moment: (moment: AccountMoment) => void };
  }
}

export interface AccountLayerProps {
  playerId: string | null;
  /** The `accounts` feature is on for this player. */
  enabled: boolean;
  /** The age check is passed and the game has opened. */
  ready: boolean;
  /** This launch is the player's first visit: no offers yet. */
  firstVisit: boolean;
  /** True when nothing else is on screen or in play. */
  canInterrupt: () => boolean;
}

/**
 * The optional account (producer 2026-10-14): offered at good moments,
 * created and signed in by a link we email, and the link itself when the
 * game opens from one. The board is drawn in Pixi (engine
 * `widgets/account`); only the email field is the page's own input, laid
 * over the board's field.
 */
export function AccountLayer({
  playerId,
  enabled,
  ready,
  firstVisit,
  canInterrupt,
}: AccountLayerProps) {
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [view, setView] = useState<AccountView | null>(null);
  const [email, setEmail] = useState('');
  const [field, setField] = useState<FieldRect | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const dialog = useRef<AccountDialogHandle | null>(null);
  const viewRef = useRef<AccountView | null>(null);
  viewRef.current = view;
  const emailRef = useRef(email);
  emailRef.current = email;
  const offeredThisLaunch = useRef(false);
  const statusRef = useRef(status);
  statusRef.current = status;
  const canInterruptRef = useRef(canInterrupt);
  canInterruptRef.current = canInterrupt;

  const loadStatus = useCallback(async (): Promise<void> => {
    if (!playerId) return;
    try {
      const s = await fetchAccountStatus(playerId);
      setStatus(s);
    } catch {
      /* offline or an older server: no accounts this time */
    }
  }, [playerId]);

  useEffect(() => {
    if (enabled && playerId) void loadStatus();
  }, [enabled, playerId, loadStatus]);

  useEffect(() => {
    publishAccountStatus(enabled ? status : null);
  }, [status, enabled]);

  // The board: mounted while there is something to show.
  const open = view !== null;
  useEffect(() => {
    if (!open || !host.current) return;
    const handle = mountAccountDialog(host.current, {
      art: ART,
      plates: BUTTON_PLATES,
      view: viewRef.current!,
      onPress: (press) => pressRef.current(press),
      onField: setField,
    });
    dialog.current = handle;
    return () => {
      handle.destroy();
      dialog.current = null;
    };
  }, [open]);
  useEffect(() => {
    if (view) dialog.current?.show(view);
  }, [view]);

  const show = useCallback((next: AccountView | null) => setView(next), []);
  const asking = view?.mode === 'create' || view?.mode === 'signin';
  const emailValid = EMAIL.test(email.trim());
  useEffect(() => {
    if (!asking) return;
    setView((v) => (v && v.emailValid !== emailValid ? { ...v, emailValid } : v));
  }, [asking, emailValid]);

  const sendLink = useCallback(async (): Promise<void> => {
    const v = viewRef.current;
    const address = emailRef.current.trim();
    if (!v || v.busy || !EMAIL.test(address)) return;
    setView({ ...v, busy: true });
    try {
      await requestSignInLink(address, v.marketing === true);
      setView({ mode: 'sent', email: address, linkMinutes: LINK_MINUTES });
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      const message =
        status === 429
          ? "You've asked for a few links already. Try again in an hour."
          : status === 400
            ? "That doesn't look like an email address. Check it and try again."
            : status === 503
              ? "Accounts aren't available just now. Try again later."
              : undefined;
      setView({ mode: 'error', message });
    }
  }, []);

  const press = useCallback(
    (what: AccountPress): void => {
      const v = viewRef.current;
      if (!v) return;
      if (what === 'send') {
        void sendLink();
      } else if (what === 'marketing') {
        if (v.mode === 'manage' && playerId) {
          const next = !v.marketing;
          setView({ ...v, marketing: next });
          void setMarketingOptIn(playerId, next)
            .then((a) => setStatus((s) => (s ? { ...s, account: a } : s)))
            .catch(() => setView((cur) => (cur ? { ...cur, marketing: !next } : cur)));
        } else if (v.mode === 'create' || v.mode === 'signin') {
          setView({ ...v, marketing: !v.marketing });
        }
      } else if (what === 'new-link') {
        setEmail('');
        setView({ mode: 'signin', marketing: false, emailValid: false });
      } else {
        if (v.busy) return;
        if (what === 'cancel' && v.mode === 'create') writeOfferRecord(recordDismissed(readOfferRecord()));
        if (v.mode === 'created' || v.mode === 'welcome' || v.mode === 'manage') void loadStatus();
        show(null);
      }
    },
    [playerId, sendLink, show, loadStatus],
  );
  const pressRef = useRef(press);
  pressRef.current = press;

  // Settings opens the board: the account, or one to create it or sign in.
  const openBoard = useCallback((): void => {
    const a = statusRef.current?.account;
    setEmail('');
    if (a) show({ mode: 'manage', email: a.email, marketing: a.marketingOptIn });
    else show({ mode: 'signin', marketing: false, emailValid: false });
  }, [show]);
  useEffect(() => onOpenAccountBoard(openBoard), [openBoard]);

  useEffect(() => {
    window.__accounts = { open: openBoard, moment: noteAccountMoment };
    return () => {
      delete window.__accounts;
    };
  }, [openBoard]);

  // Good moments: offer the account once the moment has played out and the
  // game is between spins with nothing else open.
  useEffect(() => {
    const offer = (moment: AccountMoment): void => {
      if (!enabled || firstVisit || offeredThisLaunch.current) return;
      const s = statusRef.current;
      if (!s?.available || s.account) return;
      if (!offerAllowed(readOfferRecord(), Date.now())) return;
      offeredThisLaunch.current = true;
      const started = Date.now();
      // The game must be free on two looks in a row: one board can follow
      // another a moment later (a friend says hello once a restore's card
      // and moment have gone), and the offer waits for that one too.
      let quiet = 0;
      const attempt = (): void => {
        if (viewRef.current) {
          offeredThisLaunch.current = false;
          return;
        }
        quiet = canInterruptRef.current() ? quiet + 1 : 0;
        if (quiet >= 2) {
          writeOfferRecord(recordShown(readOfferRecord(), Date.now()));
          setEmail('');
          show({ mode: 'create', marketing: false, emailValid: false });
        } else if (Date.now() - started < WAIT_LIMIT_MS) {
          window.setTimeout(attempt, 600);
        } else {
          offeredThisLaunch.current = false;
        }
      };
      window.setTimeout(attempt, SETTLE_MS[moment]);
    };
    const offMoment = onAccountMoment(offer);
    return () => {
      offMoment();
    };
  }, [enabled, firstVisit, show]);

  // A sign-in link the game opened from: follow it once the player exists
  // and the age check is passed.
  const [linkToken] = useState(() => readLaunchSignInToken());
  const followed = useRef(false);
  useEffect(() => {
    if (followed.current || !linkToken || !playerId || !ready) return;
    followed.current = true;
    void (async () => {
      try {
        const result = await followSignInLink(linkToken);
        if (result.playerId !== playerId) {
          // This device plays as the account's player from now on. The next
          // start drops what it kept about the old one (see
          // forgetOldPlayerAfterSignIn), so the account's own copy loads.
          await adoptPlayerId(result.playerId, result.token);
          try {
            localStorage.setItem(ADOPTED_KEY, result.playerId);
            sessionStorage.setItem(SIGNED_IN_KEY, result.email);
          } catch {
            /* private mode */
          }
          window.location.replace('/');
          return;
        }
        void loadStatus();
        show({ mode: result.created ? 'created' : 'welcome', email: result.email });
      } catch {
        show({ mode: 'expired' });
      }
    })();
  }, [linkToken, playerId, ready, loadStatus, show]);

  // Back from that reload: say welcome.
  useEffect(() => {
    if (!ready || !playerId) return;
    let email: string | null = null;
    try {
      email = sessionStorage.getItem(SIGNED_IN_KEY);
      if (email) sessionStorage.removeItem(SIGNED_IN_KEY);
    } catch {
      /* private mode */
    }
    if (email) show({ mode: 'welcome', email });
  }, [ready, playerId, show]);

  if (!view) return null;
  return (
    <>
      <div
        ref={host}
        role="dialog"
        aria-modal="true"
        aria-label="Your account"
        data-testid="account-dialog"
        style={{ position: 'fixed', inset: 0, zIndex: 260 }}
      />
      {asking && field ? (
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Your email"
          placeholder="you@example.com"
          data-testid="account-email"
          className="account-email"
          value={email}
          disabled={view.busy === true}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            // Typing here mustn't reach the slot's keys on the window.
            e.stopPropagation();
            if (e.key === 'Enter') void sendLink();
            if (e.key === 'Escape') press('cancel');
          }}
          style={{
            position: 'fixed',
            left: field.x,
            top: field.y,
            width: field.width,
            height: field.height,
            zIndex: 261,
            boxSizing: 'border-box',
            padding: '0 16px',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#ffe8b0',
            caretColor: '#ffe48a',
            fontFamily: 'Fredoka, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: Math.max(16, Math.round(field.height * 0.36)),
          }}
        />
      ) : null}
    </>
  );
}
